import { useEffect, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../../components/Skeleton.js";
import { Link } from "react-router-dom";
import { api, ApiError, AppSettings } from "../../lib/api.js";

type ConfigResponse = {
  port?: number;
  dhis2BaseUrl?: string;
  authHeader?: string;
  authType?: string;
  workspaceId?: string;
  settings: Partial<AppSettings>;
  serviceHealth: Record<string, unknown>;
};

function row(label: string, value: React.ReactNode) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{value ?? <span className="soft">—</span>}</dd>
    </>
  );
}

export default function AdminSettings() {
  const [config, setConfig] = useState<ConfigResponse | null>(null);
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.config(),
      api.request<AppSettings>("/api/settings")
    ])
      .then(([c, s]) => { if (!cancelled) { setConfig(c); setSettings(s); } })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof ApiError ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Platform settings</h1>
          <p>Global configuration that applies across all workspaces. Tenant-specific settings live in each workspace.</p>
        </div>
      </header>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <SkeletonTable rows={5} columns={4} />
      ) : (
        <>
          <section className="section">
            <div className="section-head"><h2>Runtime</h2></div>
            <dl className="dl-kv">
              {row("API port", config?.port)}
              {row("Auth header", config?.authHeader)}
              {row("Auth type", config?.authType)}
              {row("Backend base URL", settings?.backendBaseUrl)}
              {row("Default DHIS2 base URL", settings?.dhis2BaseUrl || config?.dhis2BaseUrl)}
              {row("Active service profile", settings?.activeServiceProfile)}
              {row("Environment name", settings?.environmentName)}
              {row("Warehouse provider", settings?.warehouseProvider)}
              {row("Warehouse query limit", settings?.warehouseQueryLimit?.toLocaleString())}
            </dl>
          </section>

          <section className="section">
            <div className="section-head"><h2>Aggregate pipeline</h2></div>
            <dl className="dl-kv">
              {row("Aggregate ingestion endpoint", settings?.aggregateIngestionEndpoint)}
              {row("Aggregate metadata endpoint", settings?.aggregateMetadataEndpoint)}
              {row("Rollup refresh mode", settings?.rollupRefreshMode)}
              {row("Rollup strictness", settings?.rollupStrictness)}
              {row("Aggregate validation strictness", settings?.aggregateValidationStrictness)}
              {row("Period rollup mode", settings?.periodRollupMode)}
              {row("Org-unit rollup mode", settings?.orgUnitRollupMode)}
            </dl>
          </section>

          <section className="section">
            <div className="section-head"><h2>Authorisation</h2></div>
            <dl className="dl-kv">
              {row("Role mode", settings?.roleMode)}
              {row("Startup module", settings?.startupModule)}
            </dl>
            <p className="muted" style={{ marginTop: 8 }}>
              Per-tenant roles &amp; assignments live under{" "}
              <Link to="/admin/roles">Roles &amp; permissions</Link> and{" "}
              <Link to="/admin/user-roles">User role assignments</Link>.
            </p>
          </section>

          <section className="section">
            <div className="section-head"><h2>Service health</h2></div>
            <pre style={{ whiteSpace: "pre-wrap", background: "#f8fafc", padding: 12, borderRadius: 6 }}>
              {JSON.stringify(config?.serviceHealth ?? {}, null, 2)}
            </pre>
          </section>

          <section className="section">
            <div className="section-head"><h2>Service profiles</h2></div>
            <p className="muted">
              {settings?.serviceProfiles?.length ?? 0} profile(s) registered. Manage them on the{" "}
              <Link to="/admin/instances">DHIS2 instances</Link> page.
            </p>
          </section>

          <details className="section">
            <summary><strong>Raw settings JSON</strong></summary>
            <pre style={{ whiteSpace: "pre-wrap", background: "#f8fafc", padding: 12, borderRadius: 6, marginTop: 8 }}>
              {JSON.stringify(settings, null, 2)}
            </pre>
          </details>
        </>
      )}
    </div>
  );
}
